import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { brazilPhoneMask, isValidBrazilPhone } from '@/utils/brazil-phone';
import { formatWithMask } from 'react-native-mask-input';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/ui/loading-button';
import { ThemedText } from '@/components/themed-text';
import { useCreateProviderClient } from '@/hooks/use-clients';
import { useToastStore } from '@/store/toastStore';
import React, { useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { getApiErrorMessage } from '@/utils/api-error';

interface CreateClientModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const emptyForm = {
  username: '',
  email: '',
  phone: '',
};

export function CreateClientModal({ open, onOpenChange, onSuccess }: CreateClientModalProps) {
  const createClient = useCreateProviderClient();
  const addToast = useToastStore((s) => s.addToast);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) {
      setForm(emptyForm);
    }
  }, [open]);

  const closeModal = () => onOpenChange(false);

  const handleSubmit = async () => {
    if (!form.username.trim()) {
      addToast('Informe o nome do cliente.', 'error');
      return;
    }
    if (!isValidBrazilPhone(form.phone)) {
      addToast('Informe o celular com DDD, como (11) 99999-9999.', 'error');
      return;
    }

    try {
      await createClient.mutateAsync({
        username: form.username.trim(),
        phone: form.phone.replace(/\D/g, ''),
        ...(form.email.trim() && { email: form.email.trim() }),
      });
      addToast('Cliente criado com sucesso.', 'success');
      closeModal();
      onSuccess?.();
    } catch (err: unknown) {
      addToast(getApiErrorMessage(err, 'Não foi possível criar o cliente.'), 'error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogClose onPress={closeModal} />

        <View className="shrink">
          <DialogHeader className="mb-4">
            <DialogTitle>Novo cliente</DialogTitle>
            <DialogDescription className="mt-1">
              Cadastre um cliente para vincular a contratos e cobranças.
            </DialogDescription>
          </DialogHeader>

          <ScrollView className="max-h-80 shrink" keyboardShouldPersistTaps="handled">
            <View className="gap-4">
              <View>
                <ThemedText className="mb-1.5 text-sm text-muted-foreground">Nome *</ThemedText>
                <Input
                  value={form.username}
                  onChangeText={(t) => setForm((f) => ({ ...f, username: t }))}
                  placeholder="Nome do cliente"
                  autoCapitalize="words"
                  testID="create-client-name-input"
                />
              </View>
              <View>
                <ThemedText className="mb-1.5 text-sm text-muted-foreground">
                  E-mail (opcional)
                </ThemedText>
                <Input
                  value={form.email}
                  onChangeText={(t) => setForm((f) => ({ ...f, email: t }))}
                  placeholder="cliente@email.com"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  testID="create-client-email-input"
                />
              </View>
              <View>
                <ThemedText className="mb-1.5 text-sm text-muted-foreground">Telefone *</ThemedText>
                <Input
                  value={form.phone}
                  onChangeText={(t) =>
                    setForm((f) => ({
                      ...f,
                      phone: formatWithMask({ text: t, mask: brazilPhoneMask(t) }).masked,
                    }))
                  }
                  placeholder="(00) 00000-0000"
                  keyboardType="phone-pad"
                  testID="create-client-phone-input"
                />
              </View>
            </View>
          </ScrollView>

          <DialogFooter className="mt-6 flex-row justify-end gap-2">
            <Button variant="outline" title="Cancelar" onPress={closeModal} />
            <LoadingButton
              title="Salvar cliente"
              onPress={handleSubmit}
              loading={createClient.isPending}
              testID="create-client-save-btn"
            />
          </DialogFooter>
        </View>
      </DialogContent>
    </Dialog>
  );
}
